import React, { useEffect, useState } from 'react';
import { useObservable } from 'react-use';
import { useSelector } from 'react-redux';
import * as rxop from 'rxjs/operators';
import { InstallerStatus, installerStatus$ } from '@pos-refacto/installer-rxjs';
import { Trans, useTranslation } from 'react-i18next';

import { integrationMapper } from 'constants/installer';
import { useConfirmation } from 'components/Confirmation';
import { saveCafaConfig } from 'actions/integrations/CafaConfigs';
import { CAFA_LEVELS, INTEGRATION_TYPES, INTEGRATIONS } from 'constants/CAFA';
import { getUserLoggedIn } from 'reducers/Login';
import { getCafaEntry, getIsLoadingCafa } from 'reducers/cafaConfigs';
import { RootState, useAppDispatch } from 'reducers';

type IntegrationStatus = InstallerStatus['data']['integrations'][number]['status'];

const metricsCollectorStatus$ = installerStatus$.pipe<
  IntegrationStatus | undefined
>(
  // Typescript complains because POS and installer-rxjs have different versions of rxjs (6.6.3 vs 7.4.0)
  rxop.map<InstallerStatus, IntegrationStatus | undefined>(status => {
    const msName = integrationMapper['metrics-collector-ms'].entity;
    const metricsCollectorStatus = status?.data.integrations.find(
      integration => integration.name === msName,
    );
    return metricsCollectorStatus?.status;
  }),
);

function getDeviceInfoCollectionConsentSeen(userId: string) {
  return (state: RootState) => {
    const entry = getCafaEntry<
      typeof INTEGRATION_TYPES.global,
      typeof INTEGRATIONS.global.deviceInfoCollectionConsent,
      { acknowledged: boolean }
    >(
      INTEGRATIONS.global.deviceInfoCollectionConsent,
      INTEGRATION_TYPES.global,
      {
        level: CAFA_LEVELS.User,
        id: userId,
      },
    )(state);
    return entry?.value?.acknowledged;
  };
}

export function useDeviceInfoCollectionConsentPopup() {
  const { t } = useTranslation('dataCollectionAcknowledgment');
  const dispatch = useAppDispatch();
  const confirm = useConfirmation();

  const metricsCollectorStatus = useObservable(metricsCollectorStatus$);
  const userId = useSelector(state => getUserLoggedIn(state)?.userID);
  const isLoadingCafa = useSelector(getIsLoadingCafa);
  const wasConsentPopupSeen = useSelector(
    getDeviceInfoCollectionConsentSeen(userId),
  );

  const [hasDisplayedConsentPopup, setHasDisplayedConsentPopup] = useState(
    false,
  );

  const shouldShowConsentPopup =
    userId &&
    !isLoadingCafa &&
    !wasConsentPopupSeen &&
    !hasDisplayedConsentPopup &&
    metricsCollectorStatus &&
    metricsCollectorStatus === 'StatusRunning';

  useEffect(() => {
    if (shouldShowConsentPopup) {
      setHasDisplayedConsentPopup(true);
      confirm({
        title: t('title'),
        body: (
          <Trans i18nKey="dataCollectionAcknowledgment:body">
            <a
              href="https://wiki.erply.com/article/2060-metrics-collector-microservice"
              rel="noreferrer noopener"
              target="_blank"
            >
              the wiki article about the data collection
            </a>
          </Trans>
        ),
        confirmText: t('acceptButton'),
        noReject: true,
      }).then(() => {
        dispatch(
          saveCafaConfig({
            integrationName: INTEGRATIONS.global.deviceInfoCollectionConsent,
            integrationType: INTEGRATION_TYPES.global,
            level: { level: CAFA_LEVELS.User, id: userId },
            config: { acknowledged: true },
          }),
        );
      });
    }
  }, [dispatch, confirm, userId, shouldShowConsentPopup, t]);
}
